import { CommonModule } from '@angular/common';
import { Component, input, signal, inject } from '@angular/core';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { UserSharedService } from '../../service/user-shared.service';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { ImageCropperComponent, ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ParentIdentitySectionComponent } from '../../../identity/components/parent-identity-section/parent-identity-section.component';

@Component({
  selector: 'app-personal-info',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TranslatePipe,
    InitialsPipe,
    ImageCropperComponent,
    ParentButtonComponent,
    ParentIdentitySectionComponent,
  ],
  templateUrl: './personal-info.component.html',
  styleUrl: './personal-info.component.scss',
})
export class PersonalInfoComponent {
  user = input<AuthGen.User>();
  isUploading = signal(false);
  showCropper = signal(false);
  isImageLoading = signal(false);
  hasImageError = signal(false);
  isFileSizeError = signal(false);
  #userShared = inject(UserSharedService);
  #sanitizer: DomSanitizer = inject(DomSanitizer);

  imageChangedEvent: Event | null = null;
  croppedImage: SafeUrl = '';
  private croppedImageBlob: Blob | null = null;
  private readonly MAX_FILE_SIZE = 1024 * 1024 * 3; // 3MB in bytes

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (!file) return;

    this.showCropper.set(true);
    this.isImageLoading.set(true);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);

    // Use setTimeout to ensure the component is rendered before setting the event
    setTimeout(() => {
      this.imageChangedEvent = event;
    }, 100);

    // Add a timeout to prevent infinite loading
    setTimeout(() => {
      if (this.isImageLoading()) {
        this.isImageLoading.set(false);
        this.hasImageError.set(true);
        this.isFileSizeError.set(false);
      }
    }, 10000); // 10 second timeout
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = this.#sanitizer.bypassSecurityTrustUrl(
      event.objectUrl || '',
    );
    // Store the blob for later use
    this.croppedImageBlob = event.blob || null;
  }

  imageLoaded(image: LoadedImage) {
    // Image has loaded, hide the loader
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  cropperReady() {
    // cropper ready
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  loadImageFailed() {
    // show message
    this.isImageLoading.set(false);
    this.hasImageError.set(true);
    this.isFileSizeError.set(false);
  }

  // Method to check if image cropper is ready
  isCropperReady(): boolean {
    return (
      !this.isImageLoading() &&
      !this.hasImageError() &&
      this.imageChangedEvent !== null
    );
  }

  retryImageLoad(): void {
    this.isImageLoading.set(true);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
    // The image cropper will automatically retry when the event is still set
  }

  closeCropper(): void {
    this.showCropper.set(false);
    this.imageChangedEvent = null;
    this.croppedImage = '';
    this.croppedImageBlob = null;
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  async saveCroppedImage(): Promise<void> {
    if (!this.croppedImageBlob) return;

    try {
      this.isUploading.set(true);

      // Use the stored blob directly
      const file = new File([this.croppedImageBlob], 'avatar.png', {
        type: 'image/png',
        lastModified: Date.now(),
      });

      // Check file size
      if (file.size > this.MAX_FILE_SIZE) {
        this.hasImageError.set(true);
        this.isFileSizeError.set(true);
        this.isImageLoading.set(false);
        this.showCropper.set(true);
        return;
      }

      await this.#userShared.uploadAvatar.mutateAsync(file);
      this.closeCropper();
    } catch (error) {
      console.error('Error saving cropped image:', error);
    } finally {
      this.isUploading.set(false);
    }
  }

  async deleteAvatar(): Promise<void> {
    await this.#userShared.deleteAvatar.mutateAsync();
  }
}
